import { IPromotionsListFilters } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 10;

const PromotionService = sdk.getService('PromotionService');

interface IStore {
  page: number;
  limit: number;
  search: string;
  filter?: IPromotionsListFilters;
  setFilter: (filter?: IPromotionsListFilters) => void;
  setPageAndLimit: (page: number, limit: number) => void;
  setSearch: (search: string) => void;
}

const store = create<IStore>(set => ({
  page: INITIAL_PAGE,
  limit: INITIAL_LIMIT,
  search: '',
  setFilter: (filter?: IPromotionsListFilters) =>
    set(state => ({ ...state, page: INITIAL_PAGE, filter })),
  setPageAndLimit: (page: number, limit: number) => set(state => ({ ...state, page, limit })),
  setSearch: (search: string) => set(state => ({ ...state, search, page: INITIAL_PAGE }))
}));

export const usePromotionsList = (fetchAll = false) => {
  const { page, limit, search, filter, setFilter, setSearch, setPageAndLimit } = store();

  const queryKey = [
    'promotions',
    `page-${page}`,
    `limit-${limit}`,
    search,
    `bypass-${fetchAll}`,
    filter
  ];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => PromotionService.findAllPromotions({ page, limit }, search, fetchAll, filter)
  });

  const resetSearchResults = () => setSearch('');

  const pagination = data && {
    page: data.page,
    limit: data.limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
    nextPage: data.nextPage,
    pagingCounter: data.pagingCounter,
    prevPage: data.prevPage,
    totalDocs: data.totalDocs,
    totalPages: data.totalPages
  };

  return {
    promotions: data?.docs,
    isLoading,
    pagination,
    filter,
    setPageAndLimit,
    setSearch,
    setFilter,
    resetSearchResults
  };
};
